import React, { useCallback, useEffect, useState } from "react";
import { TextField, IconButton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Autocomplete from "@mui/material/Autocomplete";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

const DEFAULT_TYPES = [
  { id: "static", label: "Static" },
  { id: "animated", label: "Animated" },
];

const useStyles = makeStyles(() => ({
  paper: { overflowY: "hidden", fontSize: 12 },
  autocomplete: {
    "& .MuiFormLabel-root": {
      fontSize: 12,
      color: "#1E1E1E",
      fontWeight: 400,
      fontFamily: "Nunito, sans-serif !important",
      top: "-5.7px !important",
      zIndex: 2,
    },
    "& .MuiFormLabel-asterisk": { color: "#e32b35 !important" },
    "& .MuiFormLabel-root.Mui-focused": { color: "#54565B !important" },
    "& .MuiFormLabel-root.Mui-disabled": { color: "#878888 !important" },
    "& .MuiFilledInput-root.Mui-disabled": {
      border: "none",
    },
    "& .MuiInputLabel-filled.MuiInputLabel-shrink": {
      transform: " translate(9px, 12px) scale(0.75)",
      color: "#54565B",
    },

    fontFamily: "Nunito, sans-serif !important",
    "& .MuiAutocomplete-inputRoot": {
      "--end-gap": 96,
      height: 42,
      marginBottom: 8,
      fontSize: 12,
      flexWrap: "nowrap",
      position: "relative",
      boxSizing: "border-box",
    },

    "& .MuiFilledInput-input::placeholder": { color: "#8A8A8A", opacity: 1 },

    "& .MuiFilledInput-root": {
      backgroundColor: "#ffffff",
      height: 42,
      fontSize: 12,
      border: "1px solid #d4d4d4",
      paddingRight: "calc(var(--end-gap) * 1px) !important",
      paddingLeft: "8px !important",
      borderRadius: 6,
      paddingBottom: 0,
      paddingTop: 10,
      display: "flex",
      alignItems: "flex-start",
      cursor: "text",
      boxSizing: "border-box",
    },

    "& .MuiFilledInput-root.Mui-disabled, \
       & .MuiAutocomplete-inputRoot.Mui-disabled, \
       & .MuiInputBase-root.Mui-disabled": {
      backgroundColor: "#F5F6F8 !important",
      borderColor: "#E5E7EB !important",
      cursor: "not-allowed",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      color: "#6B7280 !important",
      WebkitTextFillColor: "#6B7280" /* Safari */,
    },

    "& .MuiFilledInput-input": {
      lineHeight: "20px",
      height: 20,
      paddingRight: 5,
      padding: "0 !important",
      color: "#000000",
      marginTop: 8,
      minWidth: 0,
      transition: "none",
    },

    "& .MuiFilledInput-underline :focus": { borderBottom: "none" },
    "& .MuiFilledInput-underline:before": { borderBottom: "none" },
    "& .MuiFilledInput-underline:after": { borderBottom: "none" },
    "& .MuiFilledInput-underline:hover:before": { borderBottom: "none" },
    "& .MuiAutocomplete-clearIndicator, \
       & .MuiAutocomplete-endAdornment, \
       & .MuiAutocomplete-popupIndicator": { display: "none" },
  },

  popupPaper: {
    marginTop: 6,
    borderRadius: 12,
    boxShadow:
      "0 6px 18px rgba(30, 36, 50, 0.16), 0 1px 3px rgba(30, 36, 50, 0.08)",
    overflow: "hidden",
  },
  listbox: { maxHeight: 240, padding: "6px 0", overflowY: "auto" },
  option: {
    fontSize: 14,
    lineHeight: "22px",
    padding: "10px 14px",
    // color: "#1E1E1E",
  },

  clearBtn: { padding: 4, borderRadius: 6, "& svg": { fontSize: 18 } },
  dropdownBtn: { padding: 4, borderRadius: 6, "& svg": { fontSize: 20 } },

  endAdornmentContainer: {
    position: "absolute",
    right: 4,
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 2,
    pointerEvents: "auto",
  },
}));

export default function BannerTypeFilter({
  value,
  onChange,
  label = "Banner Type",
  disabled = false,
  required = false,
  isAnimated = false,
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => setSelected(value || null), [value]);

  const handleClear = useCallback(() => {
    if (disabled || isAnimated) return;
    // Clear only the visible field value; parent selection remains unchanged.
    setSelected(null);
  }, [disabled, isAnimated]);

  const getInputProps = (params) => ({
    ...params.InputProps,
    endAdornment: disabled ? null : (
      <div className={classes.endAdornmentContainer}>
        {selected && !isAnimated && (
          <IconButton
            className={classes.clearBtn}
            size="small"
            aria-label="Clear selected banner type"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
          >
            <CloseIcon style={{ width: 18, height: 18 }} />
          </IconButton>
        )}
        <IconButton
          className={classes.dropdownBtn}
          size="small"
          aria-label="Toggle dropdown"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((p) => {
              const next = !p;
              if (next) setSelected(value || null);
              return next;
            });
          }}
        >
          <ExpandMore
            style={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
              width: 18,
              height: 18,
            }}
          />
        </IconButton>
      </div>
    ),
  });

  return (
    <div style={{ width: "100%", minWidth: 196, marginTop: 0 }}>
      <Autocomplete
        disabled={disabled}
        id="banner-type-search-and-select"
        className={`${classes.autocomplete} ${classes.paper}`}
        disablePortal={false}
        PopperProps={{
          style: { zIndex: 1600 },
        }}
        options={DEFAULT_TYPES}
        value={selected}
        open={disabled ? false : open}
        onOpen={() => {
          if (disabled) return;
          setSelected(value || null);
          setOpen(true);
        }}
        onClose={(e, reason) => {
          if (reason !== "toggleInput") setOpen(false);
        }}
        getOptionLabel={(opt) => opt?.label || ""}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        onChange={(e, newVal) => {
          if (disabled) return;
          setSelected(newVal);
          onChange(newVal);
          setOpen(false);
        }}
        classes={{
          paper: classes.popupPaper,
          listbox: classes.listbox,
          option: classes.option,
        }}
        renderOption={(props, option) => (
          <li {...props}>{option?.label || ""}</li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            variant="filled"
            onClick={() => {
              if (disabled) return;
              setSelected(value || null);
              setOpen(true);
            }}
            inputProps={{
              ...params.inputProps,
              autoComplete: "off",
              placeholder: open && !disabled ? "Search & Select" : "",
            }}
            InputProps={getInputProps(params)}
          />
        )}
      />
    </div>
  );
}
